type Token = { value: string; from: number; to: number }
type Part = {
  from: number
  to: number
  prefix: string
  dynamic: boolean
  trailingSpace: boolean
  content: PersonalizationValue
}
type Frame = { from: number; parts: Part[]; count: number; singleText?: string }

export type PersonalizationValue = (string | NestedPersonalization)[]
export type PersonalizationStage = { text: string; content: PersonalizationValue }
export type NestedPersonalization = {
  text: string
  field: PersonalizationValue
  pipelineStages: PersonalizationStage[]
}

export type PersonalizationExpression = {
  from: number
  to: number
  reference: string
  pipelineStages: PersonalizationStage[]
}

const filters = new Set([
  'dropfirstword',
  'droplastword',
  'firstword',
  'lastword',
  'lower',
  'trim',
  'upper',
  'default',
  'json',
  'add',
  'minutes',
  'hours',
  'days',
  'parsedate',
  'formatdate',
  'anon',
  'regex',
])

const newPart = (from: number): Part => ({
  from,
  to: from,
  prefix: '',
  dynamic: false,
  trailingSpace: false,
  content: [],
})

export const parsePersonalization = (source: string, references: ReadonlySet<string>): PersonalizationExpression[] => {
  const expressions: PersonalizationExpression[] = []
  const stack: Frame[] = []
  for (const token of tokenize(source)) {
    if (token.value === '{{') {
      stack.push({ from: token.from, parts: [newPart(token.to)], count: 0 })
      continue
    }
    const frame = stack[stack.length - 1]
    if (!frame) continue
    // a single text token without a dot does not terminate a call.
    if (
      token.value === '}}' &&
      (frame.count !== 1 || frame.singleText === undefined || frame.singleText.includes('.'))
    ) {
      stack.pop()
      const [key, ...stages] = frame.parts
      const call: NestedPersonalization = {
        text: source.slice(frame.from, token.to),
        field: key.content,
        pipelineStages: stages.map((stage) => ({ text: source.slice(stage.from, stage.to), content: stage.content })),
      }
      const validStages = stages.every((stage) => {
        const name = stage.prefix.split(' ')[0]
        return !stage.trailingSpace && (!stage.dynamic || stage.prefix.includes(' ')) && filters.has(name)
      })
      if (!key.dynamic && references.has(key.prefix) && validStages) {
        expressions.push({
          from: frame.from,
          to: token.to,
          reference: key.prefix,
          pipelineStages: call.pipelineStages,
        })
      }
      const parent = stack[stack.length - 1]
      if (parent) {
        parent.count += 1
        parent.singleText = undefined
        const part = parent.parts[parent.parts.length - 1]
        part.content.push(call)
        part.dynamic = true
        part.trailingSpace = false
        part.to = token.to
      }
      continue
    }
    frame.count += 1
    frame.singleText = token.value
    if (token.value === '|') {
      frame.parts.push(newPart(token.to))
      continue
    }
    const part = frame.parts[frame.parts.length - 1]
    const last = part.content.length - 1
    if (typeof part.content[last] === 'string') part.content[last] += token.value
    else part.content.push(token.value)
    if (!part.dynamic) part.prefix += token.value
    part.trailingSpace = token.value.endsWith(' ')
    part.to = token.to
  }
  // Prefer one outer label; retain known inner fields when the outer expression cannot be labeled.
  return expressions
    .sort((a, b) => a.from - b.from || b.to - a.to)
    .reduce<PersonalizationExpression[]>((result, item) => {
      if (!result.length || item.from >= result[result.length - 1].to) result.push(item)
      return result
    }, [])
}

// Preserve source spans in UTF-16 units for CodeMirror.
const tokenize = (source: string): Token[] => {
  // Match escaped braces/backslashes, runs of opening braces, closing pairs, or pipes; gaps are plain text.
  const boundaries = /\\[\\{}]|\{{2,}|\}\}|\|/g
  const tokens: Token[] = []
  let cursor = 0
  const emit = (from: number, to: number, value = source.slice(from, to)) => {
    if (to <= from) {
      return
    }
    tokens.push({ value, from, to })
  }
  for (const match of source.matchAll(boundaries)) {
    const text = match[0]
    const start = match.index
    const end = start + text.length
    if (text.startsWith('{')) {
      // An odd run leaves its first brace as ordinary text.
      const pairsStart = start + (text.length % 2)
      emit(cursor, pairsStart)
      for (let index = pairsStart; index < end; index += 2) {
        emit(index, index + 2)
      }
    } else {
      emit(cursor, start)
      emit(start, end, text.startsWith('\\') ? text[1] : text)
    }
    cursor = end
  }
  emit(cursor, source.length)
  return tokens
}
